import { IArtwork, TradePriceInfo } from '@/types'
import {
  FilterArtworkHitProps,
  ImageSizes,
  SavedArtwork,
  StrapiPagination,
} from 'types'
import { Err, getApiBasepath } from '.'

export const artworkCardTradeFields = ['price', 'currency', 'priceGbp']

export const dynamicTradeFields = [
  'adjustedPriceUk',
  'adjustedPriceUs',
  'adjustedPriceCanMex',
  'adjustedPriceEu1',
  'adjustedPriceEu2',
  'adjustedPriceMeast',
  'adjustedPriceApac',
  'adjustedPriceRow',
]

export const editionTradesPopulation = {
  highestBidUk: true,
  highestBidUs: true,
  highestBidEu1: true,
  highestBidEu2: true,
  highestBidMeast: true,
  highestBidApac: true,
  highestBidCanMex: true,
  highestBidRow: true,
  lowestAskUk: true,
  lowestAskUs: true,
  lowestAskCanMex: true,
  lowestAskEu1: true,
  lowestAskEu2: true,
  lowestAskMeast: true,
  lowestAskApac: true,
  lowestAskRow: true,
}

export const artworkCardPopulation = {
  fields: ['name'],
  populate: {
    artists: {
      fields: ['name'],
    },
    images: {
      fields: ['name', 'formats', 'url'],
    },
    editions: {
      fields: ['name'],
      populate: {
        lowestAsk: {
          fields: artworkCardTradeFields,
        },
        highestBid: {
          fields: artworkCardTradeFields,
        },
        ...editionTradesPopulation,
      },
    },
  },
}

interface BuildArtworkCardPopulationConfig {
  withFeatures?: boolean
  withIncludes?: boolean
}

export function buildArtworkCardPopulation(
  config: BuildArtworkCardPopulationConfig = {}
) {
  return {
    fields: ['name'],
    populate: {
      artists: {
        fields: ['name'],
      },
      images: {
        fields: ['name', 'formats', 'url'],
      },
      editions: {
        fields: ['name'],
        populate: {
          lowestAsk: {
            fields: artworkCardTradeFields,
          },
          highestBid: {
            fields: artworkCardTradeFields,
          },
          features: config.withFeatures,
          includes: config.withIncludes,
          ...editionTradesPopulation,
        },
      },
    },
  }
}

export const artworkPagePopulation = {
  fields: [
    'id',
    'name',
    'medium',
    'releaseDate',
    'isFullDate',
    'royalty',
    'spreadPercentage',
    'upcomingRelease',
  ],
  populate: {
    gallery: {
      fields: ['name'],
    },
    images: {
      fields: ['name', 'formats', 'url'],
    },
    editions: {
      fields: [
        'name',
        'otherName',
        'material',
        'editionSize',
        'mainPrice',
        'currency',
        'information',
        'shipmentCategory',
        'createdAt',
      ],
      populate: {
        asks: {
          fields: [
            'status',
            'price',
            'priceGbp',
            'adjustedPriceUk',
            'adjustedPriceUs',
            'adjustedPriceCanMex',
            'adjustedPriceEu1',
            'adjustedPriceEu2',
            'adjustedPriceMeast',
            'adjustedPriceApac',
            'adjustedPriceRow',
            'currency',
          ],
        },
        bids: {
          fields: [
            'status',
            'price',
            'priceGbp',
            'adjustedPriceUk',
            'adjustedPriceUs',
            'adjustedPriceCanMex',
            'adjustedPriceEu1',
            'adjustedPriceEu2',
            'adjustedPriceMeast',
            'adjustedPriceApac',
            'adjustedPriceRow',
            'currency',
          ],
        },
        // highestBid: {
        //   fields: artworkCardTradeFields,
        // },
        // lowestAsk: {
        //   fields: artworkCardTradeFields,
        // },
        size: {
          fields: '*',
        },
        includes: {
          fields: '*',
        },
        features: {
          fields: '*',
        },
        additionalCertification: {
          fields: '*',
        },
        ...editionTradesPopulation,
      },
    },
    artists: {
      fields: ['name'],
      populate: {
        nationalities: {
          fields: ['name', 'alpha2Code'],
        },
      },
    },
  },
}

type SimilarArtworksResponse = {
  data: FilterArtworkHitProps[] | null
  err?: string
}

type Response = {
  data: IArtwork[]
  meta: {
    pagination: StrapiPagination
  }
}

type SaveArtworkRes = {
  id: number
  error?: Err
}

type GetSavedArtworksRes = {
  results: {
    id: number
    artwork: SavedArtwork
  }[]
  pagination: StrapiPagination
}

type IsArtworkSavedRes = {
  error?: Err
  id?: number
}

type GetBooleanValueRes = {
  data?: boolean
  error?: Err
}

export const getArtworks = async (query?: string) => {
  const response = await fetch(`${getApiBasepath()}/api/artworks?${query}`)
  const data = await response.json()
  return data as Response
}

export const getArtworksLatestSearch = async (
  searchIds: string[],
  pageSize?: number
) => {
  const response = await fetch(
    `${getApiBasepath()}/api/artworks/latest-search`,
    {
      method: 'POST',
      body: JSON.stringify({ searchIds, pageSize: pageSize || 30 }),
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )
  const data = await response.json()
  return data as Response
}
export const getArtworksFromArtist = async (
  artworkId: number | null,
  artistId: number
) => {
  const response = await fetch(
    `${getApiBasepath()}/api/artworks/more-from-artist/${artworkId}/${artistId}`
  )
  const data = await response.json()
  return data as Response
}
export const getArtworksLatestAsks = async (pageSize = 30) => {
  const response = await fetch(`${getApiBasepath()}/api/artworks/latest-asks`, {
    method: 'POST',
    body: JSON.stringify({ pageSize }),
    headers: {
      'Content-Type': 'application/json',
    },
  })
  const data = await response.json()
  return data as Response
}
export const getArtworksLatestBids = async () => {
  const response = await fetch(`${getApiBasepath()}/api/artworks/latest-bids`, {
    method: 'POST',
  })
  const data = await response.json()
  return data as Response
}
export const getArtworksNewRelease = async () => {
  const response = await fetch(
    `${getApiBasepath()}/api/artworks/new-releases`,
    {
      method: 'POST',
    }
  )
  const data = await response.json()
  return data as Response
}
export const getArtworksByMedium = async (medium: 'Print' | 'Sculpture') => {
  const response = await fetch(`${getApiBasepath()}/api/artworks/medium`, {
    method: 'POST',
    body: JSON.stringify({ medium }),
    headers: {
      'Content-Type': 'application/json',
    },
  })
  const data = await response.json()
  return data as Response
}
export const getArtistArtworks = async (
  artistId: number,
  page: number,
  pageSize: number,
  sortBy?: string,
  medium?: string
) => {
  const response = await fetch(`${getApiBasepath()}/api/artworks/artist`, {
    method: 'POST',
    body: JSON.stringify({
      artistId,
      medium: medium === 'All' ? null : medium,
      page,
      pageSize,
      sortBy,
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  })
  const data = await response.json()
  return data as Response
}
export const getArtworksPrices = async (
  pageSize = 30,
  artworkIds: number[]
) => {
  const response = await fetch(`${getApiBasepath()}/api/artworks/prices`, {
    method: 'POST',
    body: JSON.stringify({ pageSize, artworkIds }),
    headers: {
      'Content-Type': 'application/json',
    },
  })
  const data = await response.json()
  return data as Response
}

export const getSimilarArtworks = async (artworkId: number) => {
  const response = await fetch(
    `${getApiBasepath()}/api/artworks/similar/${artworkId}`
  )
  const data = await response.json()
  return data as SimilarArtworksResponse
}
// TODO: migrate this to custom API for improvements
export const getArtwork = async (id?: number, query?: string) => {
  const response = await fetch(
    `${getApiBasepath()}/api/artworks/${id}?${query}`
  )
  const data = await response.json()
  return data as { data: IArtwork }
}
export const getCustomArtwork = async (id?: number) => {
  const response = await fetch(`${getApiBasepath()}/api/artworks/artwork/${id}`)
  const data = await response.json()

  return data as { data: IArtwork }
}

export const getTrendingArtworks = async () => {
  const response = await fetch(
    `${getApiBasepath()}/api/artworks/features/trending`
  )
  const data = await response.json()

  return data as {
    id: number
    artists: { id: number; name: string }[]
    editions: {
      id: number
      name: string
      highestBid?: { id?: number } & TradePriceInfo
      lowestAsk?: { id?: number } & TradePriceInfo
    }[]
    name: string
    images: ImageSizes[]
  }[]
}

export const saveArtwork = async (artworkId: number) => {
  const res = await fetch(`${getApiBasepath()}/api/save-artworks`, {
    method: 'POST',
    body: JSON.stringify({ artworkId }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
  })
  return (await res.json()) as SaveArtworkRes
}

export const getSavedArtworks = async (
  payload: {
    name?: string
    pageSize?: string
    pageNumber?: string
    sortBy?: string
  } = {}
) => {
  const query = new URLSearchParams(payload).toString()

  const res = await fetch(`${getApiBasepath()}/api/save-artworks?${query}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
  })
  const data = await res.json()
  return data as GetSavedArtworksRes
}

export const unsaveArtwork = async (id: number) => {
  const res = await fetch(`${getApiBasepath()}/api/save-artworks/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
  })
  return (await res.json()) as { error?: Err }
}

export const isArtworkSaved = async (artworkId: number) => {
  const res = await fetch(
    `${getApiBasepath()}/api/artworks/is-saved/${artworkId}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  )
  return (await res.json()) as IsArtworkSavedRes
}

export const getHasSales = async (artworkId: number) => {
  const res = await fetch(
    `${getApiBasepath()}/api/artworks/has-sales/${artworkId}`
  )
  const data = await res.json()
  return data as GetBooleanValueRes
}

export const getWasSoldRecently = async (artworkId: number) => {
  const res = await fetch(
    `${getApiBasepath()}/api/artworks/was-sold-recently/${artworkId}`
  )
  const data = await res.json()
  return data as GetBooleanValueRes
}

export const getIsTopViewed = async (artworkId: number) => {
  const res = await fetch(
    `${getApiBasepath()}/api/artworks/is-top-viewed/${artworkId}`
  )
  const data = await res.json()
  return data as GetBooleanValueRes
}

export type SubmitWorkPayload = {
  email: string
  artistName: string
  artworkName: string
  images: File[]
  releaseDate?: string
  gallery?: string
  size?: string
  editionSize?: string
  medium?: string
  material?: string
}

type SubmitWorkRes = {
  error?: Err
  success?: boolean
}

export const submitWork = async (payload: SubmitWorkPayload) => {
  const formData = new FormData()

  Object.entries(payload).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(v => formData.append(key, v))
      return
    }

    formData.append(key, value)
  })

  const res = await fetch(`${getApiBasepath()}/api/artworks/submit-work`, {
    method: 'POST',
    body: formData,
  })
  const data = await res.json()
  return data as SubmitWorkRes
}
